import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Link2,
  Sigma,
  MessageSquare,
} from "lucide-react";

import GeneralInstructionTab from "./GeneralInstructionTab.jsx";
import JoinInstructionsTab from "./JoinInstructionsTab";
import SQLExpressionsTab from "./SQLExpressionsTab";
import QueryInstructionsTab from "./QueryInstructionsTab";

const instructionTabs = [
  {
    key: "general",
    label: "General",
    icon: BookOpen,
    description: "Overall AI guidance for this database",
  },
  {
    key: "joins",
    label: "Joins",
    icon: Link2,
    description: "Define relationships between tables",
  },
  {
    key: "expressions",
    label: "Expressions",
    icon: Sigma,
    description: "Business measures, dimensions & filters",
  },
  {
    key: "queries",
    label: "Reference Queries",
    icon: MessageSquare,
    description: "Curated question → SQL examples",
  },
];

const TABLEAU_RESTRICTED_TABS = new Set(["expressions", "queries"]);

function normalizeDbType(dbType) {
  return String(dbType || "").trim().toLowerCase();
}

export default function InstructionsTab({ configId, dbType, initialActiveTab = "general" }) {
  const isTableau = normalizeDbType(dbType) === "tableau";
  const availableTabs = useMemo(
    () => instructionTabs.filter(({ key }) => !(isTableau && TABLEAU_RESTRICTED_TABS.has(key))),
    [isTableau],
  );
  const availableTabKeys = useMemo(
    () => new Set(availableTabs.map(({ key }) => key)),
    [availableTabs],
  );
  const resolveTab = useCallback(
    (tabKey) => (availableTabKeys.has(tabKey) ? tabKey : availableTabs[0]?.key || "general"),
    [availableTabKeys, availableTabs],
  );
  const resolvedInitialTab = resolveTab(initialActiveTab);
  const [activeTab, setActiveTab] = useState(resolvedInitialTab);

  useEffect(() => {
    setActiveTab(resolveTab(initialActiveTab));
  }, [initialActiveTab, resolveTab]);

  useEffect(() => {
    if (!availableTabKeys.has(activeTab)) {
      setActiveTab(resolveTab(initialActiveTab));
    }
  }, [activeTab, availableTabKeys, initialActiveTab, resolveTab]);

  const renderActiveTab = () => {
    switch (activeTab) {
      case "general":
        return <GeneralInstructionTab sourceDbId={configId} />;

      case "joins":
              return <JoinInstructionsTab sourceDbId={configId} />;

      case "expressions":
             return <SQLExpressionsTab sourceDbId={configId} dbType={dbType} />;

      case "queries":
              return <QueryInstructionsTab sourceDbId={configId} dbType={dbType} />;
      
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">

      {/*  HEADER  */}
      <div className="flex-shrink-0  bg-background">
        <div className="px-6 py-2">
          

          <div className="inline-flex bg-muted/50 p-1 rounded-md border  border-border">
            {availableTabs.map(({ key, label, icon: Icon }) => {
              const active = activeTab === key;

              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`
                    flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all
                    ${
                      active
                        ? "bg-background shadow text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>

          {isTableau && (
            <p className="mt-3 text-xs text-muted-foreground">
              Tableau uses general instructions only. Expressions and Q&amp;A are hidden for this connection.
            </p>
          )}
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div 
        className={`flex-1 min-h-0 overflow-y-auto ${activeTab !== "general" ? "" : "no-scrollbar"}`} 
        style={activeTab !== "general" ? { scrollbarWidth: "auto" } : {}}
      >
        <div className={`px-6 ${activeTab !== "general" ? "pt-2 pb-8" : "py-6 h-full"}`}>
          {renderActiveTab()}
        </div>
      </div>
    </div>
  );
}
